"use client";

import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

interface VideoAudioValue {
	/** Whether the group is muted. Always starts muted — autoplay demands it. */
	muted: boolean;
	/** Flip the whole group. Must be called from a real user gesture. */
	toggle: () => void;
	/** Add a video to the group. Returns its unregister function. */
	register: (el: HTMLVideoElement) => () => void;
}

const VideoAudioContext = createContext<VideoAudioValue | null>(null);

/**
 * Groups the videos in one section under a single mute state, so a
 * `VideoAudioToggle` rendered anywhere inside can control them together.
 *
 * Exists because the toggle can't live next to the `<video>` itself: in the
 * Hero the media sits inside an `aria-hidden` layer, and a focusable button in
 * there would be dropped from the accessibility tree. The provider spans both
 * layers so the button can sit in the content layer and still reach the media.
 *
 * Scope is one section. Nothing coordinates across sections, because today
 * only the Hero opts in and there is one Hero per page. If a second section
 * ever passes `sound`, two videos could be audible at once — that's the point
 * to add page-wide arbitration, not before.
 *
 * Wrapping a server-rendered subtree is fine — `children` arrives as an
 * already-rendered payload and passes straight through.
 */
export const VideoAudioProvider = ({ children }: PropsWithChildren) => {
	const [muted, setMuted] = useState(true);
	const videos = useRef<Set<HTMLVideoElement>>(new Set());

	const register = useCallback((el: HTMLVideoElement) => {
		videos.current.add(el);
		return () => {
			videos.current.delete(el);
		};
	}, []);

	const toggle = useCallback(() => {
		const next = !muted;
		setMuted(next);

		if (next) return;

		// Unmuting and play() have to happen inside this click handler's own
		// call stack to count as a user gesture — deferring to the effect in
		// VideoPlayer would land outside it and the browser would refuse. That
		// effect then simply agrees with what we set here.
		for (const el of videos.current) {
			el.muted = false;
			// Autoplay can still refuse (e.g. a low media-engagement profile).
			el.play().catch(() => {});
		}
	}, [muted]);

	const value = useMemo<VideoAudioValue>(
		() => ({ muted, toggle, register }),
		[muted, toggle, register],
	);

	return <VideoAudioContext.Provider value={value}>{children}</VideoAudioContext.Provider>;
};

/**
 * For the toggle button. Throws outside a provider — a toggle with nothing to
 * toggle is a wiring bug, not a valid state.
 */
export const useVideoAudio = () => {
	const value = useContext(VideoAudioContext);
	if (!value) throw new Error("useVideoAudio must be used within a VideoAudioProvider");
	return value;
};

/**
 * For `VideoPlayer`. Returns `null` outside a provider rather than throwing,
 * because most videos on the site are decorative, silent, and have no provider
 * above them.
 */
export const useVideoAudioRegistration = () => useContext(VideoAudioContext);
